import { Grid } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import ConfirmationModal from "@/components/app/ConfirmationModal";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import Input from "@/components/Input";
import MaskInput from "@/components/MaskInput";
import SideSheetDrawer from "@/components/SidesheetDrawer";
import {
  ZIP_CODE_MESSAGE,
  ZIP_CODE_REGEX,
} from "@/constants/ProviderGroupFormInitialValues";
import {
  ALPHANUMERIC_TEXT_MESSAGE,
  ALPHANUMERIC_TEXT_REGEX,
  LETTERS_ONLY_MESSAGE,
  LETTERS_ONLY_REGEX,
  NPI_REGEX,
  PHONE_MASK,
  PHONE_MASKED_LENGTH,
  REFERRING_AGENCY_CONTACT_FIELDS,
  REFERRING_AGENCY_DETAIL_FIELDS,
  REFERRING_AGENCY_FIELD_LENGTH,
  REFERRING_AGENCY_INITIAL_VALUES,
  REFERRING_AGENCY_MESSAGES,
  REFERRING_AGENCY_SECTION,
  REFERRING_AGENCY_TITLE,
  ReferringAgencyFormValues,
  ReferringAgencyTextField,
} from "@/constants/referring-agency-constant";
import useDisclosure from "@/hooks/use-disclosure";
import { useSnackbar } from "@/hooks/useSnackbar";
import {
  useCreateReferringAgency,
  useUpdateReferringAgency,
} from "@/queries/use-referring-agency";
import { ReferringAgency } from "@/services";
import { ApiEnvelope } from "@/types/sdk-types";
import { toReferringAgencyPayload } from "@/utils/referring-agency-utils";
import { getErrorSnackbar } from "@/utils/utils";
import AddressSection from "./AddressSection";
import FormSectionCard from "./FormSectionCard";

interface UpsertReferringAgencyDrawerProps {
  open: boolean;
  onClose: () => void;
  preFillValues?: ReferringAgencyFormValues;
  onSave?: (agency?: ReferringAgency) => void;
}

type LengthRange = { min: number; max: number };

const alphanumericTextSchema = ({ min, max }: LengthRange) =>
  Yup.string()
    .trim()
    .required()
    .matches(ALPHANUMERIC_TEXT_REGEX, ALPHANUMERIC_TEXT_MESSAGE)
    .min(min)
    .max(max);

const lettersOnlySchema = ({ min, max }: LengthRange) =>
  Yup.string()
    .trim()
    .required()
    .matches(LETTERS_ONLY_REGEX, LETTERS_ONLY_MESSAGE)
    .min(min)
    .max(max);

const referringAgencyValidationSchema = Yup.object({
  agencyId: alphanumericTextSchema(
    REFERRING_AGENCY_FIELD_LENGTH.AGENCY_ID
  ).label("Agency ID"),
  name: alphanumericTextSchema(REFERRING_AGENCY_FIELD_LENGTH.NAME).label(
    "Agency Name"
  ),
  agencyType: alphanumericTextSchema(
    REFERRING_AGENCY_FIELD_LENGTH.AGENCY_TYPE
  ).label("Agency Type"),
  npi: Yup.string().matches(NPI_REGEX, REFERRING_AGENCY_MESSAGES.NPI_INVALID),
  contactPerson: lettersOnlySchema(
    REFERRING_AGENCY_FIELD_LENGTH.CONTACT_PERSON
  ).label("Contact Person"),
  contactRole: lettersOnlySchema(
    REFERRING_AGENCY_FIELD_LENGTH.CONTACT_ROLE
  ).label("Contact Role"),
  phone: Yup.string()
    .required()
    .length(PHONE_MASKED_LENGTH, REFERRING_AGENCY_MESSAGES.PHONE_INVALID)
    .label("Phone Number"),
  email: Yup.string().email().label("Email Address"),
  fax: Yup.string().length(
    PHONE_MASKED_LENGTH,
    REFERRING_AGENCY_MESSAGES.FAX_INVALID
  ),
  address: Yup.object({
    zipcode: Yup.string().matches(ZIP_CODE_REGEX, ZIP_CODE_MESSAGE),
  }),
});

const UpsertReferringAgencyDrawer = ({
  open,
  onClose,
  preFillValues,
  onSave,
}: UpsertReferringAgencyDrawerProps) => {
  const isEditMode = !!preFillValues;
  const showSnackbar = useSnackbar();
  const discardDisclosure = useDisclosure(false);
  const { mutateAsync: createAgency } = useCreateReferringAgency();
  const { mutateAsync: updateAgency } = useUpdateReferringAgency();

  const formik = useFormik({
    initialValues: preFillValues ?? REFERRING_AGENCY_INITIAL_VALUES,
    validationSchema: referringAgencyValidationSchema,
    onSubmit,
  });

  async function onSubmit(values: ReferringAgencyFormValues) {
    const payload = toReferringAgencyPayload(values);

    try {
      const response = isEditMode
        ? await updateAgency(payload)
        : await createAgency(payload);
      showSnackbar({
        type: "success",
        message: isEditMode
          ? REFERRING_AGENCY_MESSAGES.UPDATED
          : REFERRING_AGENCY_MESSAGES.ADDED,
      });
      onSave?.((response as ApiEnvelope<ReferringAgency>)?.data);
      onClose();
    } catch (error) {
      showSnackbar(getErrorSnackbar(error));
    }
  }

  function handleClose() {
    if (formik.dirty) {
      discardDisclosure.open();
      return;
    }
    onClose();
  }

  function renderTextField(field: ReferringAgencyTextField) {
    return (
      <Grid item xs={12} md={6} key={field.name}>
        <Input
          label={field.label}
          required={field.required}
          textFieldProps={{
            name: field.name,
            value: formik.values[field.name],
            onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            placeholder: `Enter ${field.label}`,
          }}
          error={formik.touched[field.name] && formik.errors[field.name]}
        />
      </Grid>
    );
  }

  return (
    <SideSheetDrawer
      open={open}
      onClose={handleClose}
      title={
        isEditMode ? REFERRING_AGENCY_TITLE.EDIT : REFERRING_AGENCY_TITLE.ADD
      }
      w="60vw"
      footerActions={
        <StyledButtonV2
          label="Save"
          onClick={() => formik.handleSubmit()}
          disabled={formik.isSubmitting}
        />
      }
    >
      <Grid container spacing={2}>
        <FormSectionCard title={REFERRING_AGENCY_SECTION.AGENCY_DETAILS}>
          {REFERRING_AGENCY_DETAIL_FIELDS.map(renderTextField)}
        </FormSectionCard>

        <FormSectionCard title={REFERRING_AGENCY_SECTION.CONTACT_DETAILS}>
          {REFERRING_AGENCY_CONTACT_FIELDS.map(renderTextField)}
          <Grid item xs={12} md={6}>
            <MaskInput
              required
              mask={PHONE_MASK}
              label="Phone Number"
              name="phone"
              placeholder="Enter Phone Number"
              value={formik.values.phone}
              onChange={(e) => formik.setFieldValue("phone", e.target.value)}
              onBlur={formik.handleBlur}
              error={formik.touched.phone && formik.errors.phone}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <MaskInput
              mask={PHONE_MASK}
              label="Fax Number"
              name="fax"
              placeholder="Enter Fax Number"
              value={formik.values.fax}
              onChange={(e) => formik.setFieldValue("fax", e.target.value)}
              onBlur={formik.handleBlur}
              error={formik.touched.fax && formik.errors.fax}
            />
          </Grid>
        </FormSectionCard>

        <AddressSection formik={formik} formikKey="address" />
      </Grid>

      <ConfirmationModal
        disclosureHost={discardDisclosure}
        content={REFERRING_AGENCY_MESSAGES.DISCARD}
        confirmationText="Discard"
        onConfirm={() => {
          discardDisclosure.close();
          onClose();
        }}
      />
    </SideSheetDrawer>
  );
};

export default UpsertReferringAgencyDrawer;
